import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  OutlinedInput,
  Checkbox,
  Stack,
  useTheme,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import { useState, useMemo, useEffect } from "react";
import Button from "../../../../../../../components/common/Button";
import { ProposalService, Service, useServices } from "./hooks/UseServices";
import { tokens } from "../../../../../../../theme";

interface AddServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (ps: ProposalService) => void;
}

export default function AddServiceDialog({
  open,
  onClose,
  onAdd,
}: AddServiceDialogProps) {
  const theme = useTheme();
    const colors = tokens(theme.palette.mode);
  const [searchQuery, setSearchQuery] = useState("");
  const [selections, setSelections] = useState<Record<number, number>>({});

  const { services, loading } = useServices("all");

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelections({});
    }
  }, [open]);

  const groupedServices = useMemo(() => {
    const filtered = services.filter((s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    return filtered.reduce(
      (acc, service) => {
        const cat =
          service.service_category_display ||
          service.service_category ||
          "Other";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(service);
        return acc;
      },
      {} as Record<string, Service[]>,
    );
  }, [services, searchQuery]);

  const handleToggle = (id: number) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = 1;
      }
      return next;
    });
  };

  const handleUpdateQty = (id: number, delta: number) => {
    setSelections((prev) => {
      const currentQty = prev[id] || 0;
      const newQty = currentQty + delta;

      const next = { ...prev };
      if (newQty <= 0) {
        delete next[id];
      } else {
        next[id] = newQty;
      }
      return next;
    });
  };

  const handleSave = () => {
    Object.entries(selections).forEach(([idStr, quantity]) => {
      const serviceId = Number(idStr);
      const serviceObj = services.find((s) => s.id === serviceId);
      if (serviceObj) {
        onAdd({ service: serviceObj, quantity });
      }
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px", p: 1 },
      }}
    >
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              color: colors.primary[100],
              mb: 0.5,
            }}
          >
            Add Additional Services
          </Typography>
          <Typography sx={{ fontSize: "13px", color: colors.primary[100], mb: 3 }}>
            Add optional services to enhance this proposal.
          </Typography>

          <OutlinedInput
            fullWidth
            placeholder="Search services"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <SearchRounded sx={{ color: colors.primary[100], mr: 1, fontSize: 20 }} />
            }
            sx={{
              bgcolor: colors.primary[800],
              borderRadius: "8px",
              height: 44,
              fontSize: "14px",
              "& fieldset": { border: "none" },
            }}
          />
        </Box>

        <Box sx={{ minHeight: 300, maxHeight: 400, overflowY: "auto", pr: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
              <CircularProgress size={28} sx={{ color: colors.primary[400] }} />
            </Box>
          ) : Object.keys(groupedServices).length === 0 ? (
            <Typography
              sx={{
                textAlign: "center",
                color: colors.primary[100],
                pt: 6,
                fontSize: "14px",
              }}
            >
              No services found.
            </Typography>
          ) : (
            Object.entries(groupedServices).map(([category, items]) => (
              <Box key={category} sx={{ mb: 3 }}>
                {/* Category Pill */}
                <Box
                  sx={{
                    display: "inline-block",
                    bgcolor: colors.primary[800],
                    color: colors.primary[400],
                    px: 1.5,
                    py: 0.4,
                    borderRadius: "16px",
                    fontSize: "11px",
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  {category}
                </Box>

                {/* Service Items */}
                <Stack spacing={1}>
                  {items.map((svc) => {
                    const qty = selections[svc.id] || 0;
                    const isSelected = qty > 0;

                    return (
                      <Box
                        key={svc.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          py: 1,
                          borderRadius: "8px",
                          bgcolor: isSelected ? "#fff8f2" : colors.primary[800],
                          border: `1px solid ${isSelected ? colors.primary[500] : "transparent"}`,
                          transition: "all 0.2s",
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggle(svc.id)}
                          sx={{
                            color: colors.primary[400],
                            "&.Mui-checked": { color: colors.primary[500] },
                            p: 1,
                          }}
                        />
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: "13px",
                            color: isSelected ? colors.primary[400] : colors.primary[100],
                            fontWeight: isSelected ? 500 : 400,
                          }}
                        >
                          {svc.name}
                        </Typography>

                        {isSelected && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mr: 3,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQty(svc.id, -1)}
                              sx={{ p: 0.2, color: colors.primary[100] }}
                            >
                              <RemoveRounded sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                width: 12,
                                textAlign: "center",
                              }}
                            >
                              {qty}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQty(svc.id, 1)}
                              sx={{ p: 0.2, color: colors.primary[100] }}
                            >
                              <AddRounded sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        )}

                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: colors.primary[100],
                            minWidth: 50,
                            textAlign: "right",
                            pr: 1,
                          }}
                        >
                          ${Number(svc.price).toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="text"
          onClick={onClose}
          sx={{ color: colors.primary[100], textTransform: "none", fontWeight: 500 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={Object.keys(selections).length === 0}
          sx={{
            bgcolor: colors.primary[400],
            color: "white",
            borderRadius: "6px",
            px: 3,
            textTransform: "none",
            fontWeight: 500,
            "&:hover": { bgcolor: colors.primary[400] },
            "&.Mui-disabled": { bgcolor: colors.primary[800], color: colors.primary[100] },
          }}
        >
          Add Services
        </Button>
      </DialogActions>
    </Dialog>
  );
}
